// src/belediye/components/mesajlasma/BasvuruAtaPanel.js
//
// FAZ-52 — "Başvuru Ata" akışı (kullanıcı kararı 2026-07-27: MEVCUT BAŞVURUYU ATA).
//
// Ne yapar: sohbette seçilen kişiye VAR OLAN bir başvuruyu atar. Yeni vatandaş/
// başvuru kaydı OLUŞTURMAZ (o iş Halk Masası'nın; burada tekrarlanmaz).
//
// Akış sonu — kullanıcı kuralı: "görev üzerinden görev açamıyoruz, yeni bir
// sayfada açılıyorsa; başvuru da aynı mantıkta ayrı olacak, ama görevler
// sayfasında değil BAŞVURU sayfasında." Bu yüzden atama başarılı olunca sohbet
// kapanır ve BAŞVURU DETAY sayfasına gidilir. Başvurunun kendi sohbeti
// (Conversation kind="basvuru") backend'de atama kancasıyla doğar
// (staff_app.views._sync_basvuru_sohbeti → mesajlasma_app.services.basvuru_chat)
// ve sohbet listesinde "Başvurular" sekmesinde görünür.

import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ArrowLeftIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";

import { ApplicationIcon } from "../icons/appIcons";
import { fetchApplicationsByStatusPage } from "../../services/belediyeApi";
import { createAssignment } from "../../redux/slices/assignmentSlice";

const SONUC_LIMIT = 8;

const kisiAdi = (t) => t?.display_name || t?.name || "";

export default function BasvuruAtaPanel({ targets = [], onCancel, onDone }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [q, setQ] = useState("");
  const [sonuclar, setSonuclar] = useState(null); // null = henüz aranmadı
  const [yukleniyor, setYukleniyor] = useState(false);
  const [secili, setSecili] = useState(null);
  const [not, setNot] = useState("");
  const [atiyor, setAtiyor] = useState(false);

  // Atama modeli TEK sorumlu tutar (staff_app.ApplicationAssignment.assigned_to).
  // Görev akışındaki "ilki sorumlu" kuralıyla aynı davranış.
  const hedef = targets[0] || null;

  const ara = useCallback(async (metin) => {
    setYukleniyor(true);
    try {
      const data = await fetchApplicationsByStatusPage({
        q: metin,
        page: 1,
        pageSize: SONUC_LIMIT,
      });
      setSonuclar(Array.isArray(data?.results) ? data.results : []);
    } catch {
      setSonuclar([]);
    } finally {
      setYukleniyor(false);
    }
  }, []);

  // Panel açılır açılmaz son başvurular listelenir (arama zorunlu değil).
  useEffect(() => {
    ara("");
  }, [ara]);

  useEffect(() => {
    const t = setTimeout(() => ara(q.trim()), 320);
    return () => clearTimeout(t);
  }, [q, ara]);

  const ata = async () => {
    if (!secili || !hedef || atiyor) return;
    setAtiyor(true);
    try {
      await dispatch(
        createAssignment({
          application: secili.id,
          assigned_to: hedef.id,
          note: not.trim(),
          token: localStorage.getItem("accessToken"),
        })
      ).unwrap();
      toast.success(`Başvuru ${kisiAdi(hedef)} kişisine atandı.`);
      onDone?.(secili.id);
      navigate(`/yonetim/basvurular/${secili.id}`);
    } catch (e) {
      toast.error(typeof e === "string" ? e : "Başvuru atanamadı.");
    } finally {
      setAtiyor(false);
    }
  };

  return (
    <div className="msj-tasklauncher">
      <div className="msj-tasklauncher-head">
        <button type="button" className="msj-iconbtn" onClick={onCancel} aria-label="Geri">
          <ArrowLeftIcon width={20} />
        </button>
        <span className="msj-tasklauncher-kime">
          Kime: <b>{kisiAdi(hedef) || "—"}</b>
          {targets.length > 1 && " (başvuru yalnız ilk kişiye atanır)"}
        </span>
      </div>

      <div className="msj-wq" role="group" aria-label="Başvuru seç">
        <div className="msj-wq-head">
          <span className="msj-wq-title">Hangi başvuru atansın?</span>
        </div>

        <div className="msj-basvuru-search">
          <MagnifyingGlassIcon width={16} />
          <input
            type="text"
            className="msj-new-search"
            placeholder="Takip no ya da konu ile ara…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>

        {yukleniyor && <div className="msj-empty">Aranıyor…</div>}
        {!yukleniyor && Array.isArray(sonuclar) && sonuclar.length === 0 && (
          <div className="msj-empty">Eşleşen başvuru yok.</div>
        )}

        <ul className="msj-wq-options">
          {(sonuclar || []).map((b) => (
            <li key={b.id}>
              <button
                type="button"
                className={`msj-wq-opt ${secili?.id === b.id ? "is-selected" : ""}`}
                onClick={() => setSecili(b)}
              >
                <span className="msj-wq-num" aria-hidden="true">
                  <ApplicationIcon />
                </span>
                <span className="msj-wq-lbl">
                  {b.tracking_number ? `#${b.tracking_number}` : `Başvuru ${b.id}`}
                  <span className="msj-wq-hint">
                    {b.subject || b.title || b.category_name || "—"}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>

        {secili && (
          <div className="msj-basvuru-onay">
            <input
              type="text"
              className="msj-new-search"
              placeholder="Not (opsiyonel)"
              maxLength={500}
              value={not}
              onChange={(e) => setNot(e.target.value)}
            />
            <button
              type="button"
              className="btn-md btn-md-primary"
              onClick={ata}
              disabled={atiyor || !hedef}
            >
              {atiyor ? "Atanıyor…" : "Ata ve başvuruya git"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
